import React, { useCallback, useEffect, useState } from "react";
import Badge from "../../../shared/TailwindComponents/Badge";
import Table from "../../../shared/TailwindComponents/Table";
import ListCountBadge from '../../../shared/TailwindComponents/ListCountBadge';
import { useTranslation } from "react-i18next";
import ProfileImage from "../../../Header/ProfileImage";
import { useHistory } from "react-router-dom";
import { fetchExternalCollaborators } from "../../../../store/actions/organization/LawfirmActions";
import { RootState, useAppDispatch } from "../../../../store";
import { useSelector } from "react-redux";
import { PiPaperPlaneTiltLight, PiShieldSlashLight, PiUserMinusLight, PiUsersLight } from "react-icons/pi";
import apis from "../../../../utils/apis";
import { deleteCaseCollaborator, resendExternalCollaboratorInvite, revokeCaseInvitation } from "../../../../store/actions/organization/ExternalPeopleActions";
import { hideModal, showModal } from "../../../../store/actions/modalActions";
import { normalise } from '../../../shared/access/accessModel';
interface ExpernalPeropleTabProps {
  getQueryParams: any
}

const ExternalPeopleTab: React.FC<ExpernalPeropleTabProps> = ({ getQueryParams }) => {
  const [externals, setExternals] = useState<any[]>([]);
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const collaboratorsPerPage = 10
  const { currentPage, total, loading } = useSelector((state: RootState) => state.employees)

  const columns = [
    'Name',
    'CaseName',
    'Status',
    'AccessLevel',
    'Actions'
  ];

  const handleRevokeAccess = async (id: number) => {
    try {
      dispatch(showModal({
        type: "confirmation",
        showModal: true,
        message: t("Are you sure you want to revoke access from this collaborator?"),
        onConfirm: async () => {
          await dispatch(revokeCaseInvitation(id))
          await refetchExternalCoollaborators()
        },
        onCancel: hideModal
      }))
      await apis.post(`/cases/invitations/revoke`, { id });
    } catch (err: any) {
      console.error("Error revoking access", err);
    }
  };

  const handleDeleteCollaborator = async (id: number) => {
    try {
      dispatch(showModal({
        type: "confirmation",
        showModal: true,
        message: t("Are you sure you want to remove this collaborator?"),
        onConfirm: async () => {
          await dispatch(deleteCaseCollaborator(id))
          await refetchExternalCoollaborators()
        },
        onCancel: hideModal
      }))
      
    } catch (err: any) {
      console.error("Error deleting collaborator", err);
    }
  };

  const handleResendInvite = async (id: number) => {
    try {
      await dispatch(resendExternalCollaboratorInvite(id))
      await refetchExternalCoollaborators()
    } catch (err: any) {
      console.error("Error resending invitation", err);
    }
    
  };

  const tableActions = [
    {
      label: t('Revoke Access'),
      onClick: (row: any) => handleRevokeAccess(row.id),
      icon: <PiShieldSlashLight className="h-4 w-4" />, 
    },
    {
      label: t('Delete Collaborator'),
      onClick: (row: any) => handleDeleteCollaborator(row.id),
      icon: <PiUserMinusLight className="h-4 w-4" />, 
    },
    {
      label: t('Resend Invite'),
      onClick: (row: any) => handleResendInvite(row.id),
      icon: <PiPaperPlaneTiltLight className="h-4 w-4" />, 
    },
  ];

  const refetchExternalCoollaborators = useCallback(async () => {
    const { searchTerm,  sortBy, sortOrder, page, limit } = getQueryParams();
    const resp = await dispatch(fetchExternalCollaborators({ search: searchTerm, sortBy, sortOrder, page, limit }));
    setExternals(resp || []);
    setPage(currentPage);
    setHasMore(resp.length === collaboratorsPerPage && currentPage < Math.ceil(total / collaboratorsPerPage));
  }, [dispatch, getQueryParams]);

  useEffect(() => {
    refetchExternalCoollaborators()
  }, [refetchExternalCoollaborators])

  const loadMoreEmployees = useCallback(async () => {
    if (!hasMore || loading) return;
    const next = page + 1;
    const { searchTerm, sortBy, sortOrder, limit } = getQueryParams();

    const resp = await dispatch(fetchExternalCollaborators({ search: searchTerm, sortBy, sortOrder, page: next, limit }));

    setExternals(prev => [...prev, ...(resp || [])]);
    setPage(currentPage);
    setHasMore(resp.length === collaboratorsPerPage && currentPage < Math.ceil(total / collaboratorsPerPage));
  }, [dispatch, page, getQueryParams, hasMore, loading]);

  const data: any[] = externals
    .map((ex): any => {
      if (!ex) {
        console.warn('Employee is undefined:', ex);
        return null;
      }
      const fullName = `${ex.FirstName?.[0]}${ex.LastName?.[0]}`.trim() || t('No name');
      return {
        id: ex.ExternalID,
        Name: (
          <div className="flex items-center space-x-2">
            <ProfileImage profilePicture={normalise(ex).avatar} email={ex.Email} initials={fullName} />
            <div>
              <span>{`${ex.FirstName} ${ex.LastName}`.trim() || t('No name')}</span>
              <p className="text-xs text-gray-600 dark:text-gray-400">{ex.Email}</p>
            </div>
          </div>
        ),
        "Status": ex.InvitationStatus === "pending" ? <Badge color="yellow">{t('INVITED')}</Badge> : ex.InvitationStatus === "accepted" ? <Badge color="green">{ex.InvitationStatus?.toUpperCase()}</Badge> :(ex.InvitationStatus === "rejected" || ex.InvitationStatus === "revoked"  || ex.InvitationStatus === "failed") && <Badge color="red">{ex.InvitationStatus?.toUpperCase()}</Badge>,
        'CaseName': ex?.CaseName,
        'AccessLevel': ex?.AccessLevel
          ? ex.AccessLevel.charAt(0).toUpperCase() + ex.AccessLevel.slice(1).toLowerCase()
          : "N/A",
      };
    })
    .filter((item): item is any => item !== null);

  return (
    /* Measured so the table fills its host rather than sizing to its rows. */
    <div className="flex flex-col flex-1 min-h-0">

      {/* No filter bar in this view - see ListCountBadge. */}
      <ListCountBadge
        showing={externals.length}
        total={total || externals.length}
        label="external collaborators"
      />
      <Table
        columns={columns}
        data={data}
        onRowClick={(row) => {
          const employee = externals.find((emp) => emp.ExternalID === row.id);
          if (employee) {
            history.push(`/external/${employee.ExternalID}`);
          }
        }}
        hasMore={hasMore}
        isLoadingMore={loading}
        onLoadMore={loadMoreEmployees}
        actions={tableActions}
        total_items={total || externals.length}
        total_showing_items={externals.length}
        setIsShowing={true}
        showingLabel={t('external collaborators')}
        emptyState={{
          icon: <PiUsersLight />,
          headline: t('You have no external collaborators listed.'),
          subtext: t('Initiate by inviting collaborators from where ever you want, track progress, and collaborate with your team.'),
          button: {
            label: t('Add Externals'),
            onClick: (() => history.push('/people/?tab=external')),
          }
        }}
      />

    </div>
  );
};

export default ExternalPeopleTab;
